import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIPaginatedSelectInput,
    UIPaginatedSelectOption,
    UIText
} from '@/ui';
import { useFetchTeamOptions } from '@/companies/queries';
import { useBulkAssignDeviceTeamAction } from '../actions';
import { useDevicePermissions, useDevicesTranslation } from '../hooks';

export type BulkSetTeamModalProps = {
    companyId: string;
    deviceIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onApplied: () => void;
};

export function BulkSetTeamModal(props: BulkSetTeamModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: BulkSetTeamModalProps): ReactElement {
    const { companyId, deviceIds, onClose, onApplied } = props;
    const { t } = useDevicesTranslation();
    const { bulkAssignDeviceTeam, loading } = useBulkAssignDeviceTeamAction({
        companyId
    });

    // Which teams the devices may be assigned to. Company owners/admins can pick
    // any team; a team admin can only assign to the teams they administer. Other
    // teams are still listed but disabled with an explanation.
    //
    // Answered from my own team roles rather than the loaded page, so it holds
    // for any team id whether or not it is in the current window.
    const { allowedTeamIds } = useDevicePermissions({ companyId });
    const isTeamAllowed = useCallback(
        (teamId: string) =>
            allowedTeamIds === null || allowedTeamIds.includes(teamId),
        [allowedTeamIds]
    );

    const [team, setTeam] = useState<UIPaginatedSelectOption | undefined>(
        undefined
    );
    const [showError, setShowError] = useState(false);

    const teamPicker = useFetchTeamOptions({ companyId });
    const teamOptions = useMemo<UIPaginatedSelectOption[]>(
        () =>
            teamPicker.options.map((option) => {
                const disabled = !isTeamAllowed(option.value);
                return {
                    ...option,
                    disabled,
                    subLabel: disabled
                        ? t('shared.fields.team.disabled_not_admin')
                        : undefined
                };
            }),
        [teamPicker.options, isTeamAllowed, t]
    );

    function onApply() {
        if (!team || !isTeamAllowed(team.value)) {
            setShowError(true);
            return;
        }
        bulkAssignDeviceTeam({ deviceIds, teamId: team.value })
            .then(() => onApplied())
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('shared.devices.set_team_label')}
                subtitle={
                    deviceIds.length > 1
                        ? t('shared.devices.bulk_applies_to', {
                              count: deviceIds.length
                          })
                        : undefined
                }
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <UIPaginatedSelectInput
                        label={t('shared.fields.team.label')}
                        placeholder={t('shared.fields.team.placeholder')}
                        searchPlaceholder={t(
                            'shared.fields.team.search_placeholder'
                        )}
                        emptyText={t('shared.fields.team.empty')}
                        errorStateText={t('shared.fields.team.load_error')}
                        value={team}
                        options={teamOptions}
                        search={teamPicker.search}
                        onSearchChange={teamPicker.setSearch}
                        isLoading={teamPicker.status === 'pending'}
                        isError={teamPicker.status === 'error'}
                        isFetchingNextPage={teamPicker.isFetchingNextPage}
                        hasNextPage={teamPicker.hasNextPage}
                        onLoadMore={teamPicker.fetchNextPage}
                        onRetry={teamPicker.refetch}
                        // Previously a selected-but-disallowed team blocked
                        // Apply while this only rendered for `!team`, leaving a
                        // dead button and no message.
                        errorText={
                            !showError
                                ? ''
                                : !team
                                  ? t('shared.fields.team.error_required')
                                  : !isTeamAllowed(team.value)
                                    ? t('shared.fields.team.error_not_allowed')
                                    : ''
                        }
                        onChange={(option) => setTeam(option)}
                    />
                    <UIText variant="small" type="default2">
                        {t('components.bulk_set_team_modal.member_note')}
                    </UIText>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.apply'),
                        loading: loading,
                        disabled: loading,
                        onClick: onApply
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
